import { supabase } from '@/integrations/supabase/client';

/**
 * ============================================================================
 * CENTRALIZED SUPABASE QUERY LAYER - SINGLE SOURCE OF TRUTH
 * ============================================================================
 * 
 * ⚠️ ALWAYS USE THIS MODULE FOR ALL SUPABASE QUERIES
 * 
 * Supabase has a 1000 row limit by default. This module automatically
 * paginates queries to fetch ALL records, preventing data truncation.
 * 
 * Usage:
 *   import { db } from '@/lib/supabaseQuery';
 *   
 *   // Fetch all records (automatically paginated)
 *   const users = await db.fetchAll('users', '*');
 *   
 *   // Fetch with filters
 *   const activeUsers = await db.fetchAll('users', '*', {
 *     filters: [{ column: 'status', operator: 'eq', value: 'active' }]
 *   });
 *   
 *   // Fetch single record
 *   const user = await db.fetchOne('users', '*', [
 *     { column: 'id', operator: 'eq', value: userId }
 *   ]);
 *   
 *   // Count records
 *   const count = await db.count('users');
 *   
 *   // Fetch top N (intentional limit)
 *   const recent = await db.fetchTop('users', '*', 10, {
 *     orderBy: { column: 'created_at', ascending: false }
 *   });
 * 
 * ============================================================================
 */

export type FilterOperator = 
  | 'eq' | 'neq' 
  | 'gt' | 'gte' | 'lt' | 'lte' 
  | 'like' | 'ilike' 
  | 'in' | 'is'
  | 'cs' | 'cd' | 'ov'  // Array operators: contains, contained_by, overlaps
  | 'not'  // Negation filter: .not(column, innerOperator, value)
  | 'or';  // OR filter: .or(conditions)

export interface QueryFilter {
  column: string;
  operator: FilterOperator;
  value: unknown;
  /** Required when operator is 'not' - specifies the inner comparison operator */
  innerOperator?: 'eq' | 'neq' | 'in' | 'is' | 'like' | 'ilike';
}

/**
 * Extended filter type for negation queries
 * Usage: { column: 'status', operator: 'not', innerOperator: 'eq', value: 'deleted' }
 */
export interface NotFilter extends QueryFilter {
  operator: 'not';
  innerOperator: 'eq' | 'neq' | 'in' | 'is' | 'like' | 'ilike';
}

/**
 * Filter for 'or' operator using PostgREST OR syntax
 * Usage: { operator: 'or', conditions: 'col1.eq.val1,col2.is.null' }
 */
export interface OrFilter {
  operator: 'or';
  conditions: string;  // PostgREST OR syntax
}

export interface QueryOptions {
  filters?: (QueryFilter | NotFilter | OrFilter)[];
  orderBy?: { column: string; ascending?: boolean };
  pageSize?: number; // Batch size for pagination (default 1000)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dynamic query builder requires flexible typing
type QueryBuilder = ReturnType<typeof supabase.from>;

/**
 * Apply filters to a Supabase query builder
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Filter values can be of various types
function applyFilters(query: QueryBuilder, filters: (QueryFilter | NotFilter | OrFilter)[]): QueryBuilder {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dynamic query chaining
  let result = query as any;
  
  filters.forEach((filter) => {
    const { operator } = filter;
    switch (operator) {
      case 'or': {
        const orFilter = filter as OrFilter;
        result = result.or(orFilter.conditions);
        break;
      }
      case 'not': {
        const notFilter = filter as NotFilter;
        result = result.not(notFilter.column, notFilter.innerOperator, notFilter.value);
        break;
      }
      default: {
        const queryFilter = filter as QueryFilter;
        const { column, value } = queryFilter;
        switch (operator) {
          // Handle NULL-safe comparisons automatically
          case 'eq': 
            if (value === null) {
              result = result.is(column, null);
            } else {
              result = result.eq(column, value);
            }
            break;
          case 'neq': 
            if (value === null) {
              // IS NOT NULL - use .not() with 'is' operator
              result = result.not(column, 'is', null);
            } else {
              result = result.neq(column, value);
            }
            break;
          case 'gt': result = result.gt(column, value); break;
          case 'gte': result = result.gte(column, value); break;
          case 'lt': result = result.lt(column, value); break;
          case 'lte': result = result.lte(column, value); break;
          case 'like': result = result.like(column, value); break;
          case 'ilike': result = result.ilike(column, value); break;
          case 'in': result = result.in(column, value); break;
          case 'is': result = result.is(column, value); break;
          case 'cs': result = result.contains(column, value); break;
          case 'cd': result = result.containedBy(column, value); break;
          case 'ov': result = result.overlaps(column, value); break;
        }
      }
    }
  });
  
  return result;
}

/**
 * SINGLE SOURCE OF TRUTH for Supabase queries
 * Always bypasses the 1000 row limit unless explicitly using fetchOne or fetchTop
 */
export const db = {
  /**
   * Fetch ALL records from a table (paginated automatically)
   * This is the primary method - use it for any query that might return more than 1000 rows
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Generic default for flexibility
  async fetchAll<T = any>(
    table: string,
    select: string,
    options: QueryOptions = {}
  ): Promise<T[]> {
    const { filters = [], orderBy, pageSize = 1000 } = options;
    
    let allData: T[] = [];
    let from = 0;
    let hasMore = true;

    while (hasMore) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dynamic table access
      let query = (supabase as any).from(table).select(select);
      
      // Apply filters
      query = applyFilters(query, filters);
      
      // Apply ordering
      if (orderBy) {
        query = query.order(orderBy.column, { ascending: orderBy.ascending ?? true });
      }
      
      // Apply pagination
      query = query.range(from, from + pageSize - 1);
      
      const { data, error } = await query;
      
      if (error) throw error;
      
      if (data && data.length > 0) {
        allData = [...allData, ...(data as T[])];
        from += pageSize;
        hasMore = data.length === pageSize;
      } else {
        hasMore = false;
      }
    }
    
    return allData;
  },

  /**
   * Fetch a single record
   * Use for queries that should return exactly one result
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Generic default for flexibility
  async fetchOne<T = any>(
    table: string,
    select: string,
    filters: QueryFilter[]
  ): Promise<T | null> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dynamic table access
    let query = (supabase as any).from(table).select(select);
    query = applyFilters(query, filters);
    // maybeSingle: zero rows returns null without an HTTP 406 console error.
    query = query.maybeSingle();
    
    const { data, error } = await query;
    
    // PGRST116 = no rows returned (not an error for optional lookups)
    if (error && error.code !== 'PGRST116') throw error;
    
    return data || null;
  },

  /**
   * Count records efficiently (uses head: true)
   */
  async count(
    table: string,
    filters: (QueryFilter | NotFilter | OrFilter)[] = []
  ): Promise<number> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dynamic table access
    // SECURITY: count with 'id' rather than '*' — some tables revoke SELECT on
    // sensitive columns (e.g. content_submissions.content_html, comment PII), and
    // a wildcard head-count is rejected with 401 on those.
    let query = (supabase as any)
      .from(table)
      .select('id', { count: 'exact', head: true });
    
    query = applyFilters(query, filters);
    
    const { count, error } = await query;
    
    if (error) throw error;
    
    return count || 0;
  },

  /**
   * Fetch with explicit limit (for "top N" queries)
   * Use when you intentionally want fewer than all records
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Generic default for flexibility
  async fetchTop<T = any>(
    table: string,
    select: string,
    limit: number,
    options: Omit<QueryOptions, 'pageSize'> = {}
  ): Promise<T[]> {
    const { filters = [], orderBy } = options;
    
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dynamic table access
    let query = (supabase as any).from(table).select(select);
    
    // Apply filters
    query = applyFilters(query, filters);
    
    // Apply ordering
    if (orderBy) {
      query = query.order(orderBy.column, { ascending: orderBy.ascending ?? true });
    }
    
    // Apply limit
    query = query.limit(limit);
    
    const { data, error } = await query;
    
    if (error) throw error;
    
    return data || [];
  },

  /**
   * Insert record(s)
   *
   * SECURITY: some tables have column-level read revokes (e.g.
   * `content_submissions.content_html`, which is paywalled and only readable via
   * the `get_publication_body` RPC). A bare `.select()` asks Postgres to return
   * every column, which fails with 42501 on those tables even when the write
   * itself is permitted. Pass `returningColumns` to narrow the representation.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Generic default for flexibility
  async insert<T = any>(
    table: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Flexible insert data
    data: any,
    options: { returning?: boolean; returningColumns?: string } = {}
  ): Promise<T | T[] | null> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dynamic table access
    const query = (supabase as any).from(table).insert(data);
    
    if (options.returning !== false) {
      const { data: result, error } = options.returningColumns
        ? await query.select(options.returningColumns)
        : await query.select();
      if (error) throw error;
      return Array.isArray(data) ? result : result?.[0] || null;
    }
    
    const { error } = await query;
    if (error) throw error;
    return null;
  },

  /**
   * Update records
   *
   * See the `insert` note above for when to pass `returningColumns`.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Generic default for flexibility
  async update<T = any>(
    table: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Flexible update data
    data: any,
    filters: QueryFilter[],
    options: { returning?: boolean; returningColumns?: string } = {}
  ): Promise<T[] | null> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dynamic table access
    let query = (supabase as any).from(table).update(data);
    query = applyFilters(query, filters);
    
    if (options.returning !== false) {
      const { data: result, error } = options.returningColumns
        ? await query.select(options.returningColumns)
        : await query.select();
      if (error) throw error;
      return result;
    }
    
    const { error } = await query;
    if (error) throw error;
    return null;
  },


  /**
   * Delete records
   */
  async delete(
    table: string,
    filters: QueryFilter[]
  ): Promise<void> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dynamic table access
    let query = (supabase as any).from(table).delete();
    query = applyFilters(query, filters);
    
    const { error } = await query;
    if (error) throw error;
  },

  /**
   * Upsert records
   *
   * See the `insert` note above for when to pass `returningColumns`.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Generic default for flexibility
  async upsert<T = any>(
    table: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Flexible upsert data
    data: any,
    options: { onConflict?: string; returning?: boolean; returningColumns?: string } = {}
  ): Promise<T | T[] | null> {
    // Build upsert options - onConflict is a parameter, NOT a chain method
    // NOTE: ignoreDuplicates is intentionally NOT supported per QA SOT
    // Slug collisions MUST surface as errors for proper QA handling
    const upsertOptions: { onConflict?: string } = {};
    if (options.onConflict) {
      upsertOptions.onConflict = options.onConflict;
    }
    
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dynamic table access
    const query = (supabase as any).from(table).upsert(data, upsertOptions);
    
    if (options.returning !== false) {
      const { data: result, error } = options.returningColumns
        ? await query.select(options.returningColumns)
        : await query.select();
      if (error) throw error;
      return Array.isArray(data) ? result : result?.[0] || null;
    }

    
    const { error } = await query;
    if (error) throw error;
    return null;
  },

  /**
   * Call a Supabase RPC function
   * Provides consistent interface for calling database functions
   * 
   * @example
   * const result = await db.rpc('my_function', { param1: 'value' });
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Generic default for flexibility
  async rpc<T = any>(
    functionName: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Flexible RPC params
    params?: Record<string, any>,
    options?: { count?: 'exact' | 'planned' | 'estimated' }
  ): Promise<T> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dynamic RPC calls
    const { data, error } = await (supabase as any).rpc(functionName, params, options);
    
    if (error) throw error;
    return data;
  },

  /**
   * Direct access to supabase client for complex queries
   * ⚠️ WARNING: This bypasses automatic pagination!
   * Only use for queries that definitely return < 1000 rows
   */
  get raw() {
    // Developer reminder only — never print on the live sites.
    if (import.meta.env.DEV) {
      console.warn(
        '[db.raw] Direct supabase access bypasses pagination. ' +
        'Ensure your query returns < 1000 rows or use db.fetchAll instead.'
      );
    }
    return supabase;
  }
};
